<!-- NOTICE: KAPTION-INTEGRATION -->
<script setup>
import { ref, watch, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KaptionConversationSearchAPI from 'dashboard/api/kaptionConversationSearch';
import ConversationItem from 'dashboard/components/ConversationItem.vue';

const props = defineProps({
  query: { type: String, default: '' },
  label: { type: String, default: '' },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
});

const { t } = useI18n();
const results = ref([]);
const nextCursor = ref(null);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const error = ref('');
let debounceTimer = null;
let requestToken = 0;

const normalizedQuery = computed(() => props.query.trim());
const hasResults = computed(() => results.value.length > 0);
const minQueryText = computed(() =>
  t('KAPTION_PLATFORM.CONVERSATION_SEARCH.MIN_QUERY')
);
const noResultsText = computed(() =>
  t('KAPTION_PLATFORM.CONVERSATION_SEARCH.NO_RESULTS')
);
const loadMoreText = computed(() =>
  t('KAPTION_PLATFORM.CONVERSATION_SEARCH.LOAD_MORE')
);

async function fetchResults({ append = false } = {}) {
  const q = normalizedQuery.value;
  if (q.length < 2) {
    results.value = [];
    nextCursor.value = null;
    error.value = '';
    return;
  }

  requestToken += 1;
  const currentToken = requestToken;
  if (append) {
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
    results.value = [];
    nextCursor.value = null;
  }

  error.value = '';

  try {
    const response = await KaptionConversationSearchAPI.search({
      q,
      cursor: append ? nextCursor.value : null,
    });

    if (currentToken !== requestToken) return;

    const payload = response.data || {};
    results.value = append
      ? [...results.value, ...(payload.results || [])]
      : payload.results || [];
    nextCursor.value = payload.meta?.next_cursor || null;
  } catch {
    if (currentToken === requestToken) {
      error.value = t('KAPTION_PLATFORM.CONVERSATION_SEARCH.ERROR');
    }
  } finally {
    if (currentToken === requestToken) {
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  }
}

watch(
  normalizedQuery,
  () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => fetchResults(), 250);
  },
  { immediate: true }
);

onBeforeUnmount(() => clearTimeout(debounceTimer));
</script>

<template>
  <div class="flex-1 overflow-y-auto bg-n-solid-1">
    <div v-if="normalizedQuery.length < 2" class="p-5 text-sm text-n-slate-11">
      {{ minQueryText }}
    </div>

    <div v-else-if="isLoading" class="flex justify-center p-6">
      <Spinner class="text-n-brand" />
    </div>

    <div v-else-if="error" class="p-5 text-sm text-n-ruby-11">
      {{ error }}
    </div>

    <div v-else-if="!hasResults" class="p-5 text-sm text-n-slate-11">
      {{ noResultsText }}
    </div>

    <div v-else class="py-0">
      <ConversationItem
        v-for="result in results"
        :key="result.id"
        :source="result.conversation"
        :label="label"
        :team-id="teamId"
        :folders-id="foldersId"
        :conversation-type="conversationType"
        :show-assignee="showAssignee"
        :search-match="result.search_match"
      />

      <div v-if="nextCursor" class="p-3">
        <Button
          slate
          faded
          sm
          class="w-full"
          :is-loading="isLoadingMore"
          :label="loadMoreText"
          @click="fetchResults({ append: true })"
        />
      </div>
    </div>
  </div>
</template>
